// ═══════════════════════════════════════════════════════════════
// CULTIVATION QUEUE DRAWER
// Path: src/components/cultivation/CultivationQueueDrawer.jsx
//
// Slide-out sidebar showing donors marked for cultivation work.
// Donors are marked from DonorDetailView's "Cultivate" button.
// "Done" state is derived from contact_logs — if a contact was
// logged after the donor was queued, they're done.
//
// Follows the NewDonorsSidebar pattern for layout/animation.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { Sprout, X, Check, Circle, Trash2, CheckCheck } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { contactLogService } from "../../services/contactLogService";

const SOURCE_LABELS = {
  recent_gifts: "Recent Gifts",
  new_donors: "New Donors",
  reactivated: "Reactivated",
  pollen_fading: "Pollen Fading",
  retention_danger: "Retention Danger",
  engagement_alerts: "Engagement Alerts",
  pipeline_pulse: "Pipeline Pulse",
  manual: "Manual",
};

const timeAgo = (dateStr) => {
  const ms = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const isDone = (queueItem, member) => {
  if (!member?.contactLog?.length) return false;
  const addedDate = new Date(queueItem.addedAt);
  return member.contactLog.some((log) => {
    const logDate = new Date(log.date || log.logDate || log.createdAt);
    return logDate >= addedDate;
  });
};

export const CultivationQueueDrawer = ({ isOpen, onClose }) => {
  const {
    palette,
    cultivationQueue,
    removeFromQueue,
    clearCompletedFromQueue,
    members,
    navigate,
  } = useApp();

  const [logsLoaded, setLogsLoaded] = useState(false);
  const [, setTick] = useState(0); // force re-render after logs load

  // Fetch contact logs for queued members when drawer opens
  const loadLogs = useCallback(async () => {
    if (!isOpen || cultivationQueue.length === 0) return;

    const needsLogs = cultivationQueue
      .map((q) => members.find((m) => m.id === q.memberId))
      .filter((m) => m && !m._logsLoaded);

    if (needsLogs.length === 0) {
      setLogsLoaded(true);
      return;
    }

    await Promise.all(
      needsLogs.map(async (member) => {
        try {
          const logs = await contactLogService.getByMember(member.id);
          member.contactLog = logs;
          member._logsLoaded = true;
        } catch (err) {
          // silent — done check will return false
        }
      })
    );

    setLogsLoaded(true);
    setTick((t) => t + 1); // trigger re-render
  }, [isOpen, cultivationQueue, members]);

  useEffect(() => {
    setLogsLoaded(false);
    loadLogs();
  }, [loadLogs]);

  if (!isOpen) return null;

  // Build display list
  const items = cultivationQueue.map((q) => {
    const member = members.find((m) => m.id === q.memberId);
    const name = member
      ? member.credentialedFullName ||
        `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
        "Unknown"
      : "(not found)";
    const done = member ? isDone(q, member) : false;

    return { ...q, member, name, done };
  });

  const doneCount = items.filter((i) => i.done).length;
  const pendingCount = items.length - doneCount;

  const handleClearDone = () => {
    const doneIds = items.filter((i) => i.done).map((i) => i.memberId);
    if (doneIds.length > 0) clearCompletedFromQueue(doneIds);
  };

  const handleClick = (item) => {
    if (!item.member) return;
    navigate("memberDetail", item.member);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 999,
          background: "rgba(0,0,0,0.15)",
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 380,
          zIndex: 1000,
          background: palette.cardBg,
          borderLeft: `1px solid ${palette.cardBorder}`,
          boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
          fontFamily: FONT_FAMILY,
          animation: "queueDrawerSlide 0.2s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 18px",
            borderBottom: `1px solid ${palette.cardBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: `linear-gradient(135deg, #10b98115, #10b98105)`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "#10b98120",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Sprout size={ICON_SIZES.md} style={{ color: "#10b981" }} />
            </div>
            <div>
              <div
                style={{
                  fontSize: FONT_SIZES.sm,
                  fontWeight: 700,
                  color: palette.text1,
                }}
              >
                Cultivation Queue
              </div>
              <div style={{ fontSize: "0.6rem", color: palette.text3 }}>
                {pendingCount} pending{doneCount > 0 ? ` · ${doneCount} done` : ""}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={ICON_SIZES.md} style={{ color: palette.text3 }} />
          </button>
        </div>

        {/* Clear Done bar */}
        {doneCount > 0 && (
          <div
            style={{
              padding: "8px 18px",
              borderBottom: `1px solid ${palette.cardBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                fontSize: "0.6rem",
                color: "#10b981",
                fontWeight: 600,
              }}
            >
              {doneCount} donor{doneCount > 1 ? "s" : ""} contacted
            </span>
            <button
              onClick={handleClearDone}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                background: "none",
                border: `1px solid ${palette.cardBorder}`,
                borderRadius: 6,
                padding: "3px 10px",
                fontSize: "0.6rem",
                fontWeight: 600,
                color: palette.text2,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
              }}
            >
              <CheckCheck size={ICON_SIZES.xs} />
              Clear Done
            </button>
          </div>
        )}

        {/* List */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "4px 0",
          }}
        >
          {items.length === 0 ? (
            <div
              style={{
                padding: "40px 24px",
                textAlign: "center",
                color: palette.text3,
                fontSize: FONT_SIZES.xs,
                lineHeight: 1.6,
              }}
            >
              No donors in your cultivation queue.
              <br />
              Browse pollen cards and click <strong>Cultivate</strong> to add
              donors here.
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.memberId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 18px",
                  cursor: item.member ? "pointer" : "default",
                  transition: "background 0.1s",
                  opacity: item.done ? 0.55 : 1,
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.background = `${palette.accent1}08`;
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.background = "transparent";
                }}
              >
                {/* Status indicator */}
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 11,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    background: item.done ? "#10b98120" : "transparent",
                    border: item.done
                      ? "2px solid #10b981"
                      : `2px solid ${palette.cardBorder}`,
                  }}
                >
                  {item.done && (
                    <Check size={ICON_SIZES.xs} style={{ color: "#10b981" }} />
                  )}
                </div>

                {/* Name + meta — clickable */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    cursor: item.member ? "pointer" : "default",
                  }}
                  onClick={() => handleClick(item)}
                >
                  <div
                    style={{
                      fontSize: FONT_SIZES.xs,
                      fontWeight: item.done ? 400 : 600,
                      color: item.done ? palette.text3 : palette.text1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      textDecoration: item.done ? "line-through" : "none",
                    }}
                  >
                    {item.name}
                  </div>
                  <div
                    style={{
                      fontSize: "0.55rem",
                      color: palette.text3,
                      display: "flex",
                      gap: 6,
                      marginTop: 1,
                    }}
                  >
                    <span>{SOURCE_LABELS[item.source] || item.source}</span>
                    <span>·</span>
                    <span>{timeAgo(item.addedAt)}</span>
                  </div>
                </div>

                {/* Remove button */}
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    removeFromQueue(item.memberId);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 4,
                    display: "flex",
                    alignItems: "center",
                    opacity: 0.4,
                    transition: "opacity 0.15s",
                    flexShrink: 0,
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.opacity = "1";
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.opacity = "0.4";
                  }}
                  title="Remove from queue"
                >
                  <Trash2 size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div
            style={{
              padding: "12px 18px",
              borderTop: `1px solid ${palette.cardBorder}`,
              fontSize: "0.6rem",
              color: palette.text3,
              textAlign: "center",
            }}
          >
            {pendingCount} pending · {doneCount} done · {items.length} total
          </div>
        )}
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes queueDrawerSlide {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};
